// Configuración → Equipo: el muro de visibilidad de conversaciones (PR 15,
// mudado aquí en CP3 L).
//
// Vivía en Operación y ahí no se entendía: quien lo leía no alcanzaba a ver que
// hablaba de Bandeja. La pregunta que responde es "qué alcanza a ver cada
// asesor", que es una pregunta sobre el EQUIPO, así que se lee junto a la lista
// de miembros y su título nombra la Bandeja.
//
// Es CONVERSACIONES y nada más desde F18: cada proceso lleva su propia
// 'Visibilidad de prospectos' en Procesos. Una conversación no tiene un proceso
// del cual heredar la política, y por eso el interruptor del espacio sobrevive.
//
// El API NO se mudó: sigue siendo GET/PATCH /me/settings/operations, con su
// mismo evento de auditoría. Solo cambió dónde se lee.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { getOperations, saveOperations, type VisibilityPolicy } from './settings-data.js'
import { Button, RadioGroup, Spinner, toast } from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'

const VISIBILITY_OPTIONS: { value: VisibilityPolicy; label: string; description: string }[] = [
  {
    value: 'all',
    label: 'Todo el equipo',
    description: 'Todo el equipo ve todas las conversaciones.',
  },
  {
    value: 'assigned',
    label: 'Cada quien lo suyo',
    description:
      'Cada asesor ve solo sus conversaciones y las sin asignar. Dueño y Administrador siempre ven todo.',
  },
]

export function ConversationVisibilityCard(): JSX.Element {
  const qc = useQueryClient()
  const operations = useQuery({ queryKey: ['settings-operations'], queryFn: getOperations })
  const [visibility, setVisibility] = useState<VisibilityPolicy>('all')
  const [busy, setBusy] = useState(false)

  // Seed the visibility form from the server once it answers, and again
  // whenever the server's own answer changes (a save returns the stored truth).
  useEffect(() => {
    if (operations.data) setVisibility(operations.data.visibilityPolicy)
  }, [operations.data])

  async function save(): Promise<void> {
    setBusy(true)
    try {
      await saveOperations({ visibilityPolicy: visibility })
      await qc.invalidateQueries({ queryKey: ['settings-operations'] })
      toast.ok('Guardamos quién ve las conversaciones.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos guardar la configuración.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="mt-6 rounded-xl border border-line bg-surface p-5"
      data-testid="visibility-policy"
    >
      <h2 className="text-[13px] font-semibold text-text">
        Visibilidad de conversaciones en Bandeja
      </h2>
      <p className="mt-1 text-[12px] leading-relaxed text-text-2">
        Qué conversaciones alcanza a ver cada asesor en Bandeja. Dueño y Administrador siempre ven
        todo. La visibilidad de los prospectos se configura en cada proceso, en Procesos.
      </p>

      {operations.isError ? (
        <div className="mt-4">
          <QueryError
            message="No pudimos cargar la visibilidad de conversaciones."
            onRetry={() => void operations.refetch()}
          />
        </div>
      ) : operations.isLoading || !operations.data ? (
        <div className="mt-4">
          <Spinner label="Cargando visibilidad…" />
        </div>
      ) : (
        <>
          <div className="mt-4">
            <RadioGroup
              label="Visibilidad de conversaciones en Bandeja"
              name="visibility-policy"
              hideLabel
              value={visibility}
              options={VISIBILITY_OPTIONS}
              onChange={(v) => setVisibility(v)}
            />
          </div>

          <div className="mt-5">
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Guardando…' : 'Guardar visibilidad'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
